import React, { FC } from "react";
import { Form, Input, Modal, Typography, Tooltip } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { SendOutlined } from "@ant-design/icons";
import { AlertMessage, AlertMessageBanner } from "../alert-message";
import { Spinner } from "../spinner";

import styles from "./modal.module.css";
const { ["modal"]: modalClassName, ["form"]: formClassName } = styles as any;

export type ShareMatterStatus = "SHARING" | "SHARING_COMPLETED";

export type ShareMatterModalProps = {
  show: boolean;
  loading: boolean;
  matterTitle: string;
  clientName?: string;
  collaboratorUsers?: { id: number; displayName: string }[];
  status?: ShareMatterStatus;
  onClose: () => void;
  onShare: (data: { userIds: number[]; message: string }) => void;
  alertMessage?: AlertMessage;
  renderUsersSelect: (disabled: boolean) => React.ReactNode;
  onRemoveCollaborator: (userId: number) => void;
};

export const ShareMatterModal: FC<ShareMatterModalProps> = ({
  show,
  loading,
  matterTitle,
  clientName,
  collaboratorUsers,
  status,
  onClose,
  onShare,
  alertMessage,
  renderUsersSelect,
  onRemoveCollaborator,
}) => {
  const [form] = Form.useForm();
  const sharing = status === "SHARING";
  const shared = status === "SHARING_COMPLETED";
  const collaboratorUserIds = collaboratorUsers?.map((user) => user.id);
  const interactive = !(loading || !collaboratorUserIds || sharing || shared);

  return (
    <Modal
      title="Share Matter"
      open={show}
      closable={false}
      width="70%"
      className={modalClassName}
      //style={{ top: "48px" }}
      onCancel={onClose}
      //bodyStyle={{ minHeight: "15vh", maxHeight: "70vh" }}
      okText="Share"
      okButtonProps={{
        type: "primary",
        icon: <SendOutlined />,
        onClick: () => form.submit(),
        loading: sharing,
        disabled: !interactive,
      }}
      cancelText={shared ? "Close" : "Cancel"}
      cancelButtonProps={{
        onClick: onClose,
        disabled: sharing,
      }}
    >
      {alertMessage && <AlertMessageBanner banner {...alertMessage} />}
      <Spinner show={loading} message="Loading...">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className={formClassName}
          onFinish={() => onShare(form.getFieldsValue())}
        >
          <Form.Item label="Client:">
            {clientName ? (
              <Typography.Title level={5} style={{ margin: 0 }}>
                {clientName}
              </Typography.Title>
            ) : (
              "-"
            )}
          </Form.Item>
          <Form.Item label="Matter:">
            <Typography.Title level={5} style={{ margin: 0 }}>
              {matterTitle}
            </Typography.Title>
          </Form.Item>
          <Form.Item
            label="Share with:"
            name="userIds"
            initialValue={collaboratorUserIds}
            rules={[
              {
                required: true,
                message: "Please select at least one person to share the matter with",
              },
            ]}
          >
            {renderUsersSelect(!interactive)}
          </Form.Item>
          <Form.Item label="Message:" name="message">
            <Input.TextArea placeholder="Enter a message (Optional)" />
          </Form.Item>
        </Form>
        {collaboratorUsers && collaboratorUsers.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: "1rem" }}>
              Shared with:
            </Typography.Title>
            <ul>
              {collaboratorUsers?.map((user) => (
                <li key={user.id}>
                  {user.displayName}
                  <Tooltip title="Remove collaborator" placement="right">
                    <DeleteOutlined
                      onClick={() => onRemoveCollaborator(user.id)}
                      style={{ marginLeft: "10px", color: "red" }}
                    />
                  </Tooltip>
                </li>
              ))}
            </ul>
          </>
        )}
      </Spinner>
    </Modal>
  );
};
